<template>
	<main
		class="m-service-institution"
		:class="{ 'm-service-institution--en': pageLang === 'en' }"
		:lang="pageLang"
	>
		<InstitutionDemo />

		<div class="institution-join-header">
			<div class="design-title">
				<div class="design-title-heading">
					<img class="design-title-decor design-title-decor--left" :src="titleDecorImage" alt="" />
					<h2>{{ t(`${SI_I18N_NS}.joinHeader.title`) }}</h2>
					<img class="design-title-decor" :src="titleDecorImage" alt="" />
				</div>
				<p>{{ t(`${SI_I18N_NS}.joinHeader.subtitle`) }}</p>
			</div>
		</div>

		<div class="banner">
			<div class="banner-visual">
				<img class="banner-bg" :src="bannerBgImage" alt="" />
				<img
					class="visual-main"
					:src="section1HeroImage"
					:alt="t('corporateCenter.indexPc.banner.heroImageAlt')"
				/>
			</div>

			<div class="banner-content">
				<div class="banner-title">
					{{ t("corporateCenter.indexPc.banner.titleLine1") }}
					{{ t("corporateCenter.indexPc.banner.titleLine2") }}
				</div>
				<div class="banner-desc">{{ t("corporateCenter.indexPc.banner.body") }}</div>
			</div>

			<div class="banner-actions">
				<div class="join-btn" @click="handleJoinEntryClick('hero')">
					{{ t("corporateCenter.indexPc.banner.joinCta") }}
				</div>
				<div class="contact-btn" @click="handleContactCoopClick('hero')">
					{{ t("corporateCenter.indexPc.banner.contactCta") }}
				</div>
			</div>
		</div>

		<div ref="solutionSectionRef" class="section1">
			<div class="section1-title">{{ t("corporateCenter.indexPc.section1.sectionTitle") }}</div>
			<div class="section1-card">
				<div class="card-title">
					{{ t("corporateCenter.indexPc.section1.challengeColumnTitle") }}
				</div>
				<div v-for="item in section2ChallengeList" :key="item.title" class="card-item">
					<img :src="item.icon" :alt="item.title" />
					<div class="item-text">
						<div class="item-title">{{ item.title }}</div>
						<div class="item-desc">{{ item.desc }}</div>
					</div>
				</div>
			</div>

			<div class="section1-card">
				<div class="card-title">
					{{ t("corporateCenter.indexPc.section1.solutionColumnTitle") }}
				</div>
				<div v-for="item in section2SolutionList" :key="item.title" class="card-item">
					<img :src="item.icon" :alt="item.title" />
					<div class="item-text">
						<div class="item-title">{{ item.title }}</div>
						<div class="item-desc">{{ item.desc }}</div>
					</div>
				</div>
			</div>
		</div>

		<div class="section2">
			<div class="section2-map">
				<div
					v-for="m in section2MapMarkers"
					:key="m.markerClass"
					class="map-marker"
					:class="m.markerClass"
				>
					<span class="map-marker-text"
						>{{ m.label }}<strong>{{ m.percent }}</strong></span
					>
					<span class="map-marker-dot" aria-hidden="true">
						<span class="map-marker-pulse"></span>
						<span class="map-marker-pulse map-marker-pulse--delay"></span>
					</span>
					<span class="map-marker-line" aria-hidden="true"></span>
				</div>
				<div class="map-image-box">
					<img :src="section3MapImage" :alt="t('corporateCenter.indexPc.section2.mapImageAlt')" />
				</div>
			</div>

			<div class="section2-content">
				<div class="section2-title">{{ t("corporateCenter.indexPc.section2.sectionTitle") }}</div>
				<div class="section2-desc">{{ t("corporateCenter.indexPc.section2.sectionDesc") }}</div>
				<div class="section2-metrics">
					<div v-for="item in section3Metrics" :key="item.text" class="metric-item">
						<img :src="item.icon" :alt="item.text" />
						<span>{{ item.text }}</span>
						<b>{{ item.value }}</b>
					</div>
				</div>
			</div>
		</div>

		<div class="section3">
			<div class="section3-header">
				<div class="section3-title">{{ t("corporateCenter.indexPc.section3.sectionTitle") }}</div>
			</div>

			<div class="section3-label">{{ t("corporateCenter.indexPc.section3.rowLabelTop") }}</div>
			<div class="section3-list">
				<div v-for="item in section4TopList" :key="item.title" class="section3-card">
					<div class="section3-card-head">
						<img :src="item.icon" :alt="item.title" class="section3-icon" />
						<div class="section3-card-title">{{ item.title }}</div>
					</div>
					<div class="section3-card-desc">{{ item.desc }}</div>
				</div>
			</div>

			<div class="section3-label">{{ t("corporateCenter.indexPc.section3.rowLabelBottom") }}</div>
			<div class="section3-list">
				<div v-for="item in section4BottomList" :key="item.title" class="section3-card">
					<div class="section3-card-head">
						<img :src="item.icon" :alt="item.title" class="section3-icon" />
						<div class="section3-card-title">{{ item.title }}</div>
					</div>
					<div class="section3-card-desc">{{ item.desc }}</div>
				</div>
			</div>
		</div>

		<div class="join-section">
			<h2 class="join-section-title">{{ t(`${I18N_NS}.join.sectionTitle`) }}</h2>
			<div class="join-section-top">
				<img :src="joinTopImage" :alt="t(`${I18N_NS}.join.centerImageAlt`)" />
			</div>
			<div class="join-section-steps">
				<div v-for="(step, index) in joinProcessSteps" :key="index" class="join-step-row">
					<img :src="step.icon" :alt="step.label" class="join-step-num" />
					<div class="join-step-body">
						<div class="join-step-title">{{ step.title }}</div>
						<p class="join-step-desc">{{ step.desc }}</p>
					</div>
				</div>
			</div>
		</div>

		<div class="section4">
			<div class="section4-header">
				<div class="section4-title">{{ t("corporateCenter.indexPc.section4.title") }}</div>
			</div>
			<div class="section4-showcase">
				<img :src="section5HeroImage" :alt="t('corporateCenter.indexPc.section4.imageAlt')" />
			</div>
			<p class="section4-desc">{{ t("corporateCenter.indexPc.section4.desc") }}</p>
		</div>

		<div class="section5">
			<div class="section5-content">
				<div class="section5-gradient-layer"></div>
				<div
					class="section5-image-layer"
					:style="{ backgroundImage: `url(${section6HeroImage})` }"
				></div>
				<div class="section5-left">
					<div class="section5-title">{{ t("corporateCenter.indexPc.section5.title") }}</div>
					<div class="section5-desc">{{ t("corporateCenter.indexPc.section5.desc") }}</div>
					<div class="section5-btn" @click="handleSection5KfClick">
						{{ t("corporateCenter.indexPc.section5.btn") }}
					</div>
				</div>
			</div>
		</div>

		<van-popup
			v-model:show="showContactCoopModal"
			position="center"
			round
			closeable
			close-icon-position="top-right"
			class="m-cc-contact-coop-popup"
			:style="{ width: '86%', maxWidth: '620px' }"
		>
			<div class="m-cc-contact-coop-panel">
				<h3 class="m-cc-contact-coop-title">
					{{ t(`${I18N_NS}.banner.contactCoopModal.title`) }}
				</h3>
				<p class="m-cc-contact-coop-body">{{ t(`${I18N_NS}.banner.contactCoopModal.body`) }}</p>
				<a
					class="m-cc-contact-coop-cta"
					:href="`mailto:${CONTACT_COOP_EMAIL}`"
					rel="noopener noreferrer"
					@click="trackOnboardingCta('email_contact', 'contact_modal', contactCoopCtaLabel)"
				>
					{{ contactCoopCtaLabel }}
				</a>
			</div>
		</van-popup>

		<button
			v-show="showJoinFloatBtn"
			type="button"
			class="m-cc-join-float-btn"
			:aria-label="t('corporateCenter.indexPc.banner.joinCta')"
			@click="handleJoinEntryClick('floating_cta')"
		>
			<SvgaAnimation :src="joinFloatSvgaSrc" :width="120" />
		</button>
	</main>
</template>

<script setup>
import { computed, inject, onBeforeUnmount, ref } from "vue";
import { useIntersectionObserver } from "@vueuse/core";
import { useI18n } from "~/composables/useI18n";
import InstitutionDemo from "./components/institution-demo/index.vue";
import { useServiceInstitutionTracking } from "../pc/composables/useServiceInstitutionTracking";
import { navigateCorporateCenterPage } from "~/utils/corporate-center-route";
import titleDecorImage from "../pc/image/title-decor.svg";
import SvgaAnimation from "~/components/svga-animation.vue";
import bannerBgImage from "~/pages/seer/corporate-center/index/m/images/bg.png";
import section1Image from "~/pages/seer/corporate-center/index/m/images/section1.png";
import section1ImageEn from "~/pages/seer/corporate-center/index/m/images/section1_en.png";
import section2_1 from "~/pages/seer/corporate-center/index/m/images/section2_1.png";
import section2_2 from "~/pages/seer/corporate-center/index/m/images/section2_2.png";
import section2_3 from "~/pages/seer/corporate-center/index/m/images/section2_3.png";
import section2_4 from "~/pages/seer/corporate-center/index/m/images/section2_4.png";
import section2_5 from "~/pages/seer/corporate-center/index/m/images/section2_5.png";
import section2_6 from "~/pages/seer/corporate-center/index/m/images/section2_6.png";
import section3MapImage from "~/pages/seer/corporate-center/index/m/images/map.png";
import section3_1 from "~/pages/seer/corporate-center/index/m/images/section3_1.png";
import section3_2 from "~/pages/seer/corporate-center/index/m/images/section3_2.png";
import section3_3 from "~/pages/seer/corporate-center/index/m/images/section3_3.png";
import section3_4 from "~/pages/seer/corporate-center/index/m/images/section3_4.png";
import section3_5 from "~/pages/seer/corporate-center/index/m/images/section3_5.png";
import section4_1 from "~/pages/seer/corporate-center/index/m/images/section4_1.png";
import section4_2 from "~/pages/seer/corporate-center/index/m/images/section4_2.png";
import section4_3 from "~/pages/seer/corporate-center/index/m/images/section4_3.png";
import section4_4 from "~/pages/seer/corporate-center/index/m/images/section4_4.png";
import section4_5 from "~/pages/seer/corporate-center/index/m/images/section4_5.png";
import section4_6 from "~/pages/seer/corporate-center/index/m/images/section4_6.png";
import section5Image from "~/pages/seer/corporate-center/index/m/images/section5.png";
import section5ImageEn from "~/pages/seer/corporate-center/index/m/images/section5_en.png";
import joinTopImageZh from "~/pages/seer/corporate-center/index/m/images/top.png";
import joinTopImageEn from "~/pages/seer/corporate-center/index/m/images/top_en.png";
import joinStepIcon1 from "~/pages/seer/corporate-center/index/m/images/1.png";
import joinStepIcon2 from "~/pages/seer/corporate-center/index/m/images/2.png";
import joinStepIcon3 from "~/pages/seer/corporate-center/index/m/images/3.png";
import joinStepIcon4 from "~/pages/seer/corporate-center/index/m/images/4.png";
import section6Image from "~/pages/seer/corporate-center/index/m/images/section6.png";
import section6ImageEn from "~/pages/seer/corporate-center/index/m/images/section6_en.png";
import joinFloatSvgaUrl from "~/pages/seer/corporate-center/index/pc/image/join.svga?url";
import joinFloatSvgaEnUrl from "~/pages/seer/corporate-center/index/pc/image/join_en.svga?url";

const I18N_NS = "corporateCenter.indexPc";
const SI_I18N_NS = "serviceInstitution.pc";
const { t, locale } = useI18n();
const { track } = useServiceInstitutionTracking("mobile");

const showContactCoopModal = ref(false);
const solutionSectionRef = ref(null);
const showJoinFloatBtn = ref(false);
const CONTACT_COOP_EMAIL = "tarry@seeronnet.com";
const contactCoopCtaLabel = computed(() =>
	t(`${I18N_NS}.banner.contactCoopModal.cta`, { email: CONTACT_COOP_EMAIL })
);

const openLoginPopup = inject("openLoginPopup", () => {});

const pageLang = computed(() => (locale?.value === "en" ? "en" : "zh-TW"));

const joinFloatSvgaSrc = computed(() =>
	locale?.value === "en" ? joinFloatSvgaEnUrl : joinFloatSvgaUrl
);

const section1HeroImage = computed(() =>
	locale?.value === "en" ? section1ImageEn : section1Image
);
const section5HeroImage = computed(() =>
	locale?.value === "en" ? section5ImageEn : section5Image
);
const section6HeroImage = computed(() =>
	locale?.value === "en" ? section6ImageEn : section6Image
);

const joinTopImage = computed(() => (locale?.value === "en" ? joinTopImageEn : joinTopImageZh));

const challengeIcons = [section2_1, section2_2, section2_3];
const solutionIcons = [section2_4, section2_5, section2_6];

const section2ChallengeList = computed(() =>
	challengeIcons.map((icon, i) => ({
		icon,
		title: t(`${I18N_NS}.section1.challenge.${i}.title`),
		desc: t(`${I18N_NS}.section1.challenge.${i}.desc`),
	}))
);

const section2SolutionList = computed(() =>
	solutionIcons.map((icon, i) => ({
		icon,
		title: t(`${I18N_NS}.section1.solution.${i}.title`),
		desc: t(`${I18N_NS}.section1.solution.${i}.desc`),
	}))
);

const section2MarkerLayout = [
	{ markerClass: "marker-na", percent: "15%" },
	{ markerClass: "marker-other", percent: "25%" },
	{ markerClass: "marker-sea", percent: "50%" },
	{ markerClass: "marker-jp", percent: "10%" },
];

const section2MapMarkers = computed(() =>
	section2MarkerLayout.map((row, i) => ({
		...row,
		label: t(`${I18N_NS}.section2.marker.${i}.label`),
	}))
);

const metricIcons = [section3_1, section3_2, section3_3, section3_4, section3_5];
const metricValues = [">426k", ">3786k", ">174k", ">796k", ">200"];

const section3Metrics = computed(() =>
	metricIcons.map((icon, i) => ({
		icon,
		value: metricValues[i],
		text: t(`${I18N_NS}.section2.metric.${i}.text`),
	}))
);

const section4TopIcons = [section4_1, section4_2, section4_3];
const section4BottomIcons = [section4_4, section4_5, section4_6];

const section4TopList = computed(() =>
	section4TopIcons.map((icon, i) => ({
		icon,
		title: t(`${I18N_NS}.section3.top.${i}.title`),
		desc: t(`${I18N_NS}.section3.top.${i}.desc`),
	}))
);

const section4BottomList = computed(() =>
	section4BottomIcons.map((icon, i) => ({
		icon,
		title: t(`${I18N_NS}.section3.bottom.${i}.title`),
		desc: t(`${I18N_NS}.section3.bottom.${i}.desc`),
	}))
);

const joinStepIconsM = [joinStepIcon1, joinStepIcon2, joinStepIcon3, joinStepIcon4];

const joinProcessSteps = computed(() =>
	joinStepIconsM.map((icon, i) => ({
		icon,
		label: `0${i + 1}`,
		title: t(`${I18N_NS}.join.step${i + 1}.title`),
		desc: t(`${I18N_NS}.join.step${i + 1}.desc`),
	}))
);

const handleJoinEntryClick = (pagePosition = "hero") => {
	trackOnboardingCta("join_now", pagePosition, t("corporateCenter.indexPc.banner.joinCta"));
	if (import.meta.client && localStorage.getItem("token")) {
		navigateCorporateCenterPage("/seer/corporate-center/join");
		return;
	}
	openLoginPopup(() => {
		navigateCorporateCenterPage("/seer/corporate-center/join");
	});
};

const trackOnboardingCta = (ctaId, pagePosition, ctaName = ctaId) => {
	track("institution_onboarding_cta_click", {
		cta_id: ctaId,
		cta_name: typeof ctaName === "object" && "value" in ctaName ? ctaName.value : ctaName,
		section_id: "institution_entry",
		page_position: pagePosition,
	});
};

const handleContactCoopClick = pagePosition => {
	trackOnboardingCta("contact_us", pagePosition, t("corporateCenter.indexPc.banner.contactCta"));
	showContactCoopModal.value = true;
};

const CORPORATE_SECTION5_KF_URL = "https://kf.seerq.io/?scene=11570000&lng=tc&channel=seer_6sco";

const handleSection5KfClick = () => {
	if (!import.meta.client) return;
	trackOnboardingCta(
		"customer_service_consult",
		"bottom_cta",
		t("corporateCenter.indexPc.section5.btn")
	);
	window.open(CORPORATE_SECTION5_KF_URL, "_blank", "noopener,noreferrer");
};

const { stop: stopSolutionSectionObserver } = useIntersectionObserver(
	solutionSectionRef,
	([entry]) => {
		if (!entry?.isIntersecting) return;
		track("institution_solution_section_view", {
			section_id: "solution_challenge",
			card_count: section2ChallengeList.value.length + section2SolutionList.value.length,
		});
		stopSolutionSectionObserver();
	},
	{ threshold: 0.25, rootMargin: "0px 0px -8% 0px" }
);

const { stop: stopJoinFloatSectionObserver } = useIntersectionObserver(
	solutionSectionRef,
	([entry]) => {
		if (!entry) return;
		// section1 顶部进入或越过视口顶部后展示；回到 section1 上方则隐藏
		showJoinFloatBtn.value = entry.isIntersecting || entry.boundingClientRect.top <= 0;
	},
	{ threshold: 0, rootMargin: "0px" }
);

onBeforeUnmount(() => {
	stopSolutionSectionObserver();
	stopJoinFloatSectionObserver();
});
</script>

<style lang="scss" scoped>
.m-service-institution {
	min-height: 100vh;
	-webkit-hyphens: auto;
	hyphens: auto;
}

.m-service-institution,
.m-service-institution :deep(*) {
	word-break: normal !important;
	overflow-wrap: normal !important;
	word-wrap: normal !important;
}

.institution-join-header {
	background: #fff;
	padding: 0 32px;
}

.design-title {
	padding: 0;
	text-align: center;
	box-sizing: border-box;
}

.design-title-heading {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 24px;
}

.design-title-decor {
	width: 160px;
	height: 20px;
	flex-shrink: 0;
}

.design-title-decor--left {
	transform: rotate(180deg) scaleY(-1);
}

.design-title h2 {
	margin: 0;
	color: #333;

	font-size: 40px;
	font-weight: 800;
	line-height: 60px;
}

.design-title p {
	margin: 8px auto 0;
	max-width: 100%;
	padding: 0 32px;
	color: #333;

	font-size: 24px;
	font-weight: 500;
	line-height: 39px;
	text-align: center;
}

.m-service-institution--en,
.m-service-institution--en :deep(*) {
	overflow-wrap: anywhere !important;
}

.m-service-institution--en {
	.design-title h2,
	.design-title p,
	.banner-content .banner-desc,
	.section1 .card-title,
	.section1 .item-desc,
	.section4-desc {
	}

	.design-title-heading {
		gap: 12px;
	}

	.design-title-decor {
		width: 72px;
		min-width: 0;
	}

	.design-title h2 {
		min-width: 0;
		font-size: 34px;
		line-height: 1.3;
	}

	.design-title p {
		font-size: 22px;
		line-height: 1.45;
		padding: 0 16px;
	}

	.banner-content .banner-title {
		font-size: 26px;
		line-height: 1.35;
	}

	.banner-content .banner-desc {
		font-size: 18px;
		line-height: 1.55;
	}

	.banner-actions {
		.join-btn,
		.contact-btn {
			display: flex;
			align-items: center;
			justify-content: center;
			height: auto;
			min-height: 52px;
			padding: 12px 16px;
			line-height: 1.3;
			font-size: 18px;
		}
	}

	.section1-title {
		font-size: 26px;
		line-height: 1.35;
	}

	.section1 .card-title {
		font-size: 22px;
		line-height: 1.35;
		padding: 16px 8px;
	}

	.section1 .item-title {
		font-size: 22px;
		line-height: 1.35;
	}

	.section1 .item-desc {
		font-size: 17px;
		line-height: 1.5;
	}

	.section2 .map-marker .map-marker-text {
		max-width: 148px;
		font-size: 16px;
		line-height: 1.25;
		white-space: normal;
	}

	.section2 .map-marker strong {
		display: block;
		margin-top: 2px;
		margin-left: 0;
		font-size: 16px;
	}

	.section2 .marker-other {
		left: 112px;
	}

	.section2 .marker-sea {
		right: 48px;
	}

	.section2-title {
		font-size: 26px;
		line-height: 1.35;
	}

	.section2-desc {
		font-size: 18px;
		line-height: 1.5;
	}

	.section2 .metric-item {
		align-items: flex-start;
		flex-wrap: wrap;
		font-size: 18px;
		line-height: 1.4;
		gap: 4px 8px;

		span {
			min-width: 0;
			flex: 1 1 auto;
		}

		b {
			margin-left: 0;
			flex-shrink: 0;
		}
	}

	.section3-title {
		font-size: 26px;
		line-height: 1.35;
	}

	.section3-label {
		font-size: 22px;
		line-height: 1.35;
	}

	.section3-card-title {
		font-size: 20px;
		line-height: 1.35;
		min-width: 0;
	}

	.section3-card-head {
		align-items: flex-start;
	}

	.section3-card-desc {
		font-size: 17px;
		line-height: 1.5;
	}

	.join-section-title {
		font-size: 26px;
		line-height: 1.35;
	}

	.join-step-title {
		font-size: 20px;
		line-height: 1.35;
	}

	.join-step-desc {
		font-size: 17px;
		line-height: 1.5;
	}

	.section4-title {
		font-size: 26px;
		line-height: 1.35;
	}

	.section4-desc {
		font-size: 18px;
		line-height: 1.5;
	}

	.section5-content {
		min-height: 400px;
	}

	.section5-left {
		min-width: 0;
		width: 100%;
		max-width: 64%;
	}

	.section5-gradient-layer {
		background-image: linear-gradient(
			90deg,
			rgba(255, 255, 255, 0.98) 0%,
			rgba(255, 255, 255, 0.94) 52%,
			rgba(255, 255, 255, 0.3) 82%,
			rgba(255, 255, 255, 0) 100%
		);
	}

	.section5-title {
		font-size: 26px;
		line-height: 1.35;
	}

	.section5-desc {
		font-size: 17px;
		line-height: 1.5;
	}

	.section5-btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		height: auto;
		min-height: 48px;
		max-width: 100%;
		padding: 10px 16px;
		line-height: 1.3;
		font-size: 16px;
		text-align: center;
	}

	.m-cc-contact-coop-title {
		font-size: 24px;
		line-height: 1.35;
	}

	.m-cc-contact-coop-body {
		font-size: 18px;
		line-height: 1.55;
	}

	.m-cc-contact-coop-cta {
		min-height: 64px;
		padding: 12px 20px;
		font-size: 18px;
		line-height: 1.3;
		text-align: center;
	}
}

.m-cc-join-float-btn {
	position: fixed;
	right: 16px;
	top: 50%;
	z-index: 200;
	transform: translateY(-50%);
	display: block;
	padding: 0;
	margin: 0;
	border: none;
	background: transparent;
	cursor: pointer;
	line-height: 0;
	-webkit-tap-highlight-color: transparent;
}

.m-cc-join-float-btn:active {
	transform: translateY(-50%) scale(0.96);
	filter: brightness(1.08);
}

.m-cc-join-float-btn :deep(.svga-animation) {
	display: block;
	width: 120px;
	max-width: min(120px, 24vw);
}

.banner {
	position: relative;
	overflow: hidden;
	background: linear-gradient(0deg, #fffbf3, #ffffff);
	padding-bottom: 72px;
}

.banner-bg {
	position: absolute;
	top: 0;
	right: 0;
	width: 282px !important;
	height: 373px !important;
	z-index: -1;
}

.banner-visual {
	position: relative;
	z-index: 2;
	display: flex;
	justify-content: center;
	align-items: center;
	padding-top: 40px;

	img.visual-main {
		width: min(100%, 360px);
		height: auto;
	}
}

.banner-content {
	position: relative;
	z-index: 2;
	margin: 0 auto;
	padding: 24px 32px 0;
	box-sizing: border-box;

	.banner-title {
		font-weight: bold;
		font-size: 28px;
		color: #333333;
		margin-bottom: 8px;
		line-height: 1.35;
	}

	.banner-desc {
		font-weight: 400;
		font-size: 20px;
		color: #333333;
		line-height: 1.5;
		white-space: pre-line;
	}
}

.banner-actions {
	position: relative;
	z-index: 2;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
	margin: 24px auto 0;
	padding: 0 32px;

	.join-btn,
	.contact-btn {
		width: 100%;
		max-width: 303px;
		height: 52px;
		border-radius: 8px;
		text-align: center;
		line-height: 52px;
		font-size: 20px;
		cursor: pointer;
	}

	.join-btn {
		background: #333333;
		color: #ffffff;
	}

	.contact-btn {
		background: #ffffff;
		border: 1px solid #d8d8d8;
		color: #555555;
	}
}

.section1 {
	padding: 44px 32px 34px;

	.section1-title {
		margin-bottom: 30px;

		font-weight: bold;
		font-size: 28px;
		color: #333333;
		line-height: 1.3;
	}

	.section1-card {
		background: #ffffff;
		padding: 12px 12px 8px;
		margin-bottom: 24px;
		border-radius: 12px;
		border: 1px solid #dedede;
	}

	.card-title {
		text-align: center;

		font-weight: bold;
		font-size: 24px;
		color: #333333;
		line-height: 1.4;
		padding: 18px 0 20px;
	}

	.card-item {
		display: flex;
		align-items: flex-start;
		margin-bottom: 24px;
		border-top: 1px solid #eeeeee;
		padding-top: 20px;

		img {
			width: 64px;
			height: 64px;
			flex-shrink: 0;
			margin-right: 16px;
		}
	}

	.item-text {
		flex: 1;
		min-width: 0;
	}

	.item-title {
		font-size: 22px;
		line-height: 1.35;
		font-weight: 600;
		color: #333333;
	}

	.item-desc {
		font-weight: 400;
		font-size: 18px;
		color: #a0a0a0;
		line-height: 1.5;
	}
}

.section2 {
	padding: 48px 32px 64px;
	background: linear-gradient(0deg, #fffbf3, #ffffff);

	.section2-map {
		position: relative;
		padding-top: 76px;
		margin-bottom: 28px;
	}

	.map-image-box {
		padding: 8px 10px;
		background: #fff;

		img {
			width: 100%;
			display: block;
		}
	}

	.map-marker {
		position: absolute;
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
		font-size: 20px;
		color: #333333;

		.map-marker-text {
			position: relative;
			z-index: 2;
			display: block;
			max-width: 120px;
			line-height: 1.1;
			padding: 4px;
			background: #ffffff;
			border: 1px solid #e8e8e8;
			border-radius: 10px;
			box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
		}

		strong {
			font-size: 20px;
			font-weight: 600;
			margin-left: 4px;
			color: #333333;
		}

		.map-marker-dot {
			position: relative;
			flex-shrink: 0;
			width: 12px;
			height: 12px;
			margin-top: 8px;
			border-radius: 50%;
			background: var(--marker-color);
			box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9);
			z-index: 1;
			overflow: visible;
		}

		.map-marker-line {
			width: 2px;
			flex: 0 0 120px;
			height: 120px;
			margin-top: -1px;
			background: var(--marker-color);
		}

		.map-marker-pulse {
			position: absolute;
			inset: 0;
			width: 100%;
			height: 100%;
			border-radius: 50%;
			border: 2px solid var(--marker-color);
			background: transparent;
			pointer-events: none;
			animation: mapMarkerRipple 1.8s ease-out infinite;

			&.map-marker-pulse--delay {
				animation-delay: 0.9s;
			}
		}
	}

	.marker-na {
		left: 24px;
		top: 0;
		--marker-color: #7f93e5;
	}

	.marker-other {
		left: 140px;
		top: 22px;
		--marker-color: #f08a8a;

		.map-marker-line {
			flex: 0 0 80px;
			height: 80px;
		}
	}

	.marker-sea {
		right: 72px;
		top: 45px;
		--marker-color: #f0b432;

		.map-marker-line {
			flex: 0 0 100px;
			height: 100px;
		}
	}

	.marker-jp {
		right: 8px;
		top: -50px;
		--marker-color: #b99ce0;

		.map-marker-line {
			flex: 0 0 140px;
			height: 140px;
		}
	}

	.section2-content {
		padding: 0 4px;
	}

	.section2-title {
		font-weight: bold;
		font-size: 28px;
		color: #333333;
		padding-top: 20px;
		margin-bottom: 20px;
		line-height: 1.3;
	}

	.section2-desc {
		font-size: 20px;
		color: #666666;
		margin-bottom: 20px;
		line-height: 1.5;
	}

	.section2-metrics {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}

	.metric-item {
		display: flex;
		align-items: center;
		font-size: 20px;
		line-height: 1.4;
		color: #333333;

		img {
			width: 40px;
			height: 40px;
			margin-right: 16px;
			flex-shrink: 0;
		}

		b {
			color: #f2af19;
			font-weight: 500;
			margin-left: 6px;
		}
	}
}

.section3 {
	padding: 48px 24px 34px;

	.section3-title {
		font-weight: bold;
		font-size: 28px;
		color: #333333;
		line-height: 1.3;
	}

	.section3-label {
		text-align: center;
		font-size: 24px;
		line-height: 1.4;
		color: #f0ad33;
		font-weight: 700;
		margin: 24px 0 16px;
	}

	.section3-list {
		display: flex;
		flex-direction: column;
		gap: 16px;
	}

	.section3-card {
		background: #ffffff;
		border: 1px solid #dedede;
		border-radius: 10px;
		padding: 20px 24px;
	}

	.section3-card-head {
		display: flex;
		align-items: center;
		margin-bottom: 16px;
	}

	.section3-icon {
		width: 72px;
		height: 72px;
		flex-shrink: 0;
		margin-right: 16px;
	}

	.section3-card-title {
		font-weight: bold;
		font-size: 22px;
		color: #333333;
		line-height: 1.25;
	}

	.section3-card-desc {
		font-size: 18px;
		line-height: 1.5;
		color: #a0a0a0;
	}
}

.join-section {
	padding: 32px 24px 64px;
	background: linear-gradient(0deg, #fffbf3, #ffffff);
}

.join-section-title {
	margin: 0 0 24px;
	font-weight: bold;
	font-size: 28px;
	line-height: 1.25;
	color: #333333;
}

.join-section-top {
	display: flex;
	justify-content: center;
	margin-bottom: 24px;
	margin-top: 48px;

	img {
		display: block;
		width: min(100%, 360px);
		height: auto;
	}
}

.join-section-steps {
	display: flex;
	flex-direction: column;
	gap: 24px;
	padding-top: 8px;
}

.join-step-row {
	display: flex;
	align-items: flex-start;
}

.join-step-num {
	width: 88px;
	height: auto;
	flex-shrink: 0;
	object-fit: contain;
	display: block;
	margin-right: 16px;
}

.join-step-body {
	flex: 1;
	min-width: 0;
}

.join-step-title {
	font-size: 22px;
	font-weight: 600;
	line-height: 1.25;
	color: #c67f34;
	margin-bottom: 8px;
}

.join-step-desc {
	margin: 0;
	font-size: 18px;
	line-height: 1.5;
	color: #666666;
	white-space: pre-line;
}

.section4 {
	padding: 48px 24px 32px;
	background: linear-gradient(0deg, #fffbf3, #ffffff);
}

.section4-title {
	font-weight: bold;
	font-size: 28px;
	line-height: 1.35;
	color: #333333;
	padding: 12px 0 20px;
}

.section4-showcase {
	border-radius: 12px;
	background: #ffffff;
	margin: 0 auto 20px;

	img {
		width: 100%;
		height: auto;
		display: block;
	}
}

.section4-desc {
	margin: 0;
	padding: 0 8px;

	font-weight: 400;
	font-size: 20px;
	line-height: 1.5;
	color: #333333;
	white-space: pre-line;
}

.section5 {
	padding: 24px 24px 48px;
	background: linear-gradient(0deg, #fffbf3, #ffffff);
}

.section5-content {
	position: relative;
	overflow: hidden;
	min-height: 360px;
	border-radius: 8px;
	display: flex;
	align-items: center;
	padding: 24px 20px;
	box-sizing: border-box;
}

.section5-left {
	position: relative;
	z-index: 3;
	max-width: 100%;
}

.section5-gradient-layer,
.section5-image-layer {
	position: absolute;
	inset: 0;
	background-size: cover;
	background-position: center;
	background-repeat: no-repeat;
}

.section5-gradient-layer {
	z-index: 1;
	background-image: linear-gradient(
		90deg,
		rgba(255, 255, 255, 0.95) 0%,
		rgba(255, 255, 255, 0.9) 40%,
		rgba(255, 255, 255, 0.05) 75%,
		rgba(255, 255, 255, 0) 100%
	);
}

.section5-image-layer {
	z-index: 2;
}

.section5-title {
	font-size: 28px;
	line-height: 1.3;
	color: #333333;
	font-weight: 700;
	margin-bottom: 10px;
}

.section5-desc {
	font-size: 18px;
	line-height: 1.5;
	color: #666666;
	margin-bottom: 14px;
	white-space: pre-line;
}

.section5-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 200px;
	height: 48px;
	padding: 0 20px;
	background: #333333;
	border-radius: 8px;
	color: #ffffff;
	font-size: 18px;
	cursor: pointer;
}

.m-cc-contact-coop-popup {
	:deep(.van-popup__content) {
		padding: 0;
	}
}

.m-cc-contact-coop-panel {
	padding: 40px 32px 36px;
	text-align: left;
	background: #f5f5f6;
}

.m-cc-contact-coop-title {
	margin: 0 0 20px;
	padding-right: 36px;
	font-size: 28px;
	font-weight: 700;
	color: #333333;
	line-height: 1.35;
}

.m-cc-contact-coop-body {
	margin: 0 0 32px;
	font-size: 22px;
	line-height: 1.55;
	color: #666666;
}

.m-cc-contact-coop-cta {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 80px;
	padding: 0 24px;
	border-radius: 12px;
	background: #111111;
	font-size: 22px;
	font-weight: 600;
	color: #ffffff;
	text-decoration: none;
}

@keyframes mapMarkerRipple {
	0% {
		transform: scale(1);
		opacity: 0.55;
	}
	70% {
		opacity: 0.12;
	}
	100% {
		transform: scale(3.2);
		opacity: 0;
	}
}

@media (prefers-reduced-motion: reduce) {
	.map-marker-pulse {
		animation: none !important;
	}
}
</style>
